const getCurrentPrice = require('./get-current-price')

// DEFAULT_COLUMNS is the default header and content to be displayed in our exported
// it supports both string value or object value
// if object the value is gotten from the .transform (or product[key] when omitted)
// else we just use the keys(Object.keys(DEFAULT_COLUMNS)) to extrapolate the value from product: product[key] and title
const column = (title, description, transform) => {
  const value = { title, description }
  if (transform) {
    value.transform = transform
  }
  return value
}

const STORAGE_TYPE_OPTIONS = ['cold-chain', 'non-cold-chain']

const BASIC_UNIT_OPTIONS = [
  'Ampoule',
  'Bottle',
  'Cannula',
  'Capsule',
  'Cream',
  'Dispersible tablet',
  'Drop',
  'Equipment',
  'Gallon',
  'Gel',
  'Giving set',
  'Inhaler',
  'Implant',
  'Infusion',
  'Injection',
  'Instrument set',
  'Kit',
  'Lotion',
  'Machine',
  'Nebulizer solution',
  'Needle',
  'Ointment',
  'Oral rehydration salt',
  'Pack',
  'Pair',
  'Parenteral nutrition',
  'Paste',
  'Patient',
  'Piece',
  'Plate',
  'Powder',
  'Pessary',
  'Roll',
  'Sachet',
  'Smear',
  'Solution',
  'Spray',
  'Subpack',
  'Suppository',
  'Suspension',
  'Syringe',
  'Syrup',
  'Tablet',
  'Therapeutic food',
  'Tin',
  'Tube',
  'Unit',
  'Vial',
  'Screw'
]

const DEFAULT_COLUMNS = {
  _id: column(
    'Product Id',
    'Unique product id. Uneditable, required on create.',
    product => product._id.toLowerCase()
  ),
  code: column('Code', 'Short product code (must be the same as the Product ID but without the "product:" prefix). Uneditable, required.'),
  name: column('Name', 'Short product name. Editable, required.'),
  fullName: column('Full Name', 'Full product name. Editable, required.'),
  productType: column('Product Type', 'Type of product (e.g. vaccine)'),
  storageType: column('Storage Type', `One of ${STORAGE_TYPE_OPTIONS.join(' or ')}. Editable, required.`),
  basicUnit: column('Basic Unit', `The smallest unit the product is counted in. Editable, required. If provided, must be one of: ${BASIC_UNIT_OPTIONS.join(', ')}`),
  presentation: column('Presentation', 'Pack/container size - the number of basic units in one full pack (e.g. doses per vial). Required, must be a number.'),
  prices: column(
    'Current Price',
    'The product\'s current selling price. Optional. If provided, must be a number.',
    product => getCurrentPrice(product.prices)
  ),
  maxPrice: column(
    'Current Max Price',
    'Current maximum price',
    product => getCurrentPrice(product.prices, 'maxPrice')
  ),
  buyPrice: column(
    'Current Buy Price',
    'Current buy price',
    product => getCurrentPrice(product.buyPrices, 'buyPrice')
  ),
  vat: column(
    'Current VAT',
    'Current VAT rate',
    product => getCurrentPrice(product.vats, 'vat')
  ),
  description: column('Description', 'Product description. Optional.')
}

// Only present when generating the bulk import template (opts.template), not
// in a regular export - the sheet has no changeType to report on a product.
const CHANGE_TYPE_COLUMN = column(
  'changeType',
  'Enter Create to add a new product, or Update to change an existing one. Rows with any other value, including blank, are ignored.'
)

const getColumnTitle = columnValue =>
  typeof columnValue === 'string' ? columnValue : columnValue.title

const normalizeColumn = (key, value) => {
  if (typeof value === 'object') {
    return {
      title: value.title,
      description: value.description || '',
      transform: value.transform || (product => product[key])
    }
  }
  return {
    title: value,
    description: '',
    transform: product => product[key]
  }
}

const getRows = (products, opts = {}) => {
  opts = Object.assign({
    exclude: [],
    additionalColumns: {},
    template: false
  }, opts)

  // can't exlude _id from export, since we need it for import
  opts.exclude = opts.exclude.filter(i => i !== '_id')

  const isExcluded = key => opts.exclude.indexOf(key) > -1

  const { _id, ...restDefaultColumns } = DEFAULT_COLUMNS
  // changeType goes right after Product Id when present (template is true)
  const baseColumns = opts.template
    ? { _id, changeType: CHANGE_TYPE_COLUMN, ...restDefaultColumns }
    : DEFAULT_COLUMNS
  const columns = Object.assign({}, baseColumns, opts.additionalColumns)

  const columnProps = Object.keys(columns).filter(key => !isExcluded(key))
    .map(key => normalizeColumn(key, columns[key]))

  const headers = columnProps.map(columnProp => columnProp.title)
  const descriptions = columnProps.map(columnProp => columnProp.description)
  const rows = []

  for (let product of products) {
    rows.push(columnProps.map(columnProp => columnProp.transform(product)))
  }

  return { rows, headers, descriptions }
}

module.exports = {
  DEFAULT_COLUMNS, getRows, getColumnTitle, CHANGE_TYPE_COLUMN, STORAGE_TYPE_OPTIONS, BASIC_UNIT_OPTIONS
}
